import { useMemo, useState } from 'react';
import { Box, CircularProgress, InputAdornment, TextField, Typography } from '@mui/material';
import FilterListOutlined from '@mui/icons-material/FilterListOutlined';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useFieldRestrictions } from '@/core/hooks/useFieldRestrictions';
import { loadFormSchemaModule } from '@/lib/formSchemaModules';
import { entityLabel, formFieldSections, type FormField, type FormSection } from './formFields';
import type { HelpFieldsTarget } from './helpPanelStore';
import { HelpSectionCard } from './HelpSectionCard';

/**
 * "Show field information": the fields of the screen's form, grouped by its sections, each with its help text —
 * the texts the form shows in its tooltips. Only fields the user's role may see are listed.
 */
export function HelpFieldsView({ target }: { target: HelpFieldsTarget }) {
  const { t } = useTranslation();
  const { fieldsOf: entity, module } = target;
  const { data: schemaModule, isLoading } = useQuery({
    queryKey: ['help', 'form-schema', module, entity],
    queryFn: () => loadFormSchemaModule(module, entity),
    staleTime: Infinity,
  });
  const { restrictions, isLoading: restrictionsLoading } = useFieldRestrictions(module, entity);
  const sections = useMemo(
    () => (schemaModule ? formFieldSections(schemaModule, entity, restrictions, t) : null),
    [schemaModule, entity, restrictions, t],
  );

  if (isLoading || restrictionsLoading) return <CircularProgress size={20} />;
  if (!sections) {
    return <Typography variant="body2" color="text.secondary">{t('help.fieldsUnavailable')}</Typography>;
  }

  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        {entityLabel(entity, t)}
      </Typography>
      <Typography variant="h6" component="h2" sx={{ mb: 1.5 }}>
        {t('help.fieldsTitle')}
      </Typography>
      <FieldSections sections={sections} />
    </Box>
  );
}

/** The sections with a filter over them: the first one open, the rest closed until the user opens them. */
function FieldSections({ sections }: { sections: FormSection[] }) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('');
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set(sections.slice(0, 1).map((s) => s.key)));
  const query = normalized(filter);
  const shown = query ? matchingSections(sections, query) : sections;

  const changeFilter = (value: string) => {
    setFilter(value);
    // Every section with a match opens, so each match is in sight; the user can still close one.
    const next = normalized(value);
    if (next) setOpen(new Set(matchingSections(sections, next).map((s) => s.key)));
  };

  const toggle = (key: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  return (
    <>
      <TextField
        fullWidth
        size="small"
        placeholder={t('help.filterFields')}
        value={filter}
        onChange={(e) => changeFilter(e.target.value)}
        sx={{ mb: 1.5 }}
        slotProps={{
          htmlInput: { 'aria-label': t('help.filterFields') },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <FilterListOutlined fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />
      {shown.length === 0 && (
        <Typography variant="body2" color="text.secondary">{t('help.noFieldsMatch')}</Typography>
      )}
      {shown.map((section) => (
        <FieldSectionCard key={section.key} section={section} expanded={open.has(section.key)} onToggle={() => toggle(section.key)} />
      ))}
    </>
  );
}

const normalized = (filter: string) => filter.trim().toLocaleLowerCase();

function matchingSections(sections: FormSection[], query: string): FormSection[] {
  return sections
    .map((section) => ({ ...section, fields: section.fields.filter((field) => fieldMatches(field, query)) }))
    .filter((section) => section.fields.length > 0);
}

const fieldMatches = (field: FormField, query: string) =>
  field.label.toLocaleLowerCase().includes(query) || field.description.toLocaleLowerCase().includes(query);

interface FieldSectionCardProps {
  section: FormSection;
  expanded: boolean;
  onToggle: () => void;
}

function FieldSectionCard({ section, expanded, onToggle }: FieldSectionCardProps) {
  const { t } = useTranslation();
  const count = section.fields.length;
  return (
    <HelpSectionCard title={section.title} count={count} countLabel={t('help.fieldCount', { count })} expanded={expanded} onToggle={onToggle}>
      {section.fields.map((field, index) => (
        <FieldEntry key={field.path} field={field} groupHeading={groupHeadingAt(section, index)} />
      ))}
    </HelpSectionCard>
  );
}

/** The named group a field opens, shown above it: where the group changes, unless the section bears its name. */
function groupHeadingAt(section: FormSection, index: number): string | undefined {
  const { group } = section.fields[index];
  if (!group || group === section.title || group === section.fields[index - 1]?.group) return undefined;
  return group;
}

/** A field: its label, the form's required mark, and its help text — or a muted note that it has none yet. */
function FieldEntry({ field, groupHeading }: { field: FormField; groupHeading?: string }) {
  const { t } = useTranslation();
  return (
    <>
      {groupHeading && (
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', px: 1.5, pt: 1, lineHeight: 1.6 }}>
          {groupHeading}
        </Typography>
      )}
      <Box sx={{ px: 1.5, py: 0.75 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {field.label}
          {field.required && (
            <Box component="abbr" title={t('help.required')} sx={{ color: 'error.main', textDecoration: 'none', ml: 0.25 }}>
              *
            </Box>
          )}
        </Typography>
        {field.description ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {field.description}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontStyle: 'italic' }}>
            {t('help.noDescription')}
          </Typography>
        )}
      </Box>
    </>
  );
}
